/**
 * Confirm Dialog Component
 *
 * Reusable confirmation modal for critical actions
 *
 * @module components/common/ConfirmDialog
 */

import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

/**
 * ConfirmDialog component
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback when modal is hidden
 * @param {Function} props.onConfirm - Callback when action is confirmed
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Confirmation message
 * @param {string} props.variant - Button variant (danger, warning, primary)
 * @param {string} props.confirmText - Confirm button text
 * @param {string} props.cancelText - Cancel button text
 * @param {boolean} props.requireTyping - Require user to type confirmation word
 * @param {string} props.confirmationWord - Word to type for confirmation
 */
const ConfirmDialog = ({
  show,
  onHide,
  onConfirm,
  title = 'Confirmar Acción',
  message,
  variant = 'danger',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  requireTyping = false,
  confirmationWord = 'CONFIRMAR',
}) => {
  const [typedWord, setTypedWord] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (requireTyping && typedWord !== confirmationWord) {
      return;
    }

    setLoading(true);
    try {
      await onConfirm();
      setTypedWord('');
      onHide();
    } catch (error) {
      console.error('Confirm action error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setTypedWord('');
    onHide();
  };

  const isConfirmDisabled = requireTyping && typedWord !== confirmationWord;

  return (
    <Modal show={show} onHide={handleCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {variant === 'danger' && <i className="bi bi-exclamation-triangle me-2 text-danger"></i>}
          {variant === 'warning' && <i className="bi bi-exclamation-circle me-2 text-warning"></i>}
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>{message}</p>

        {requireTyping && (
          <div className="mt-3">
            <p className="mb-2">
              <strong>Para confirmar, escribe:</strong>{' '}
              <code className="text-danger">{confirmationWord}</code>
            </p>
            <Form.Control
              type="text"
              value={typedWord}
              onChange={(e) => setTypedWord(e.target.value)}
              placeholder={`Escribe "${confirmationWord}"`}
              disabled={loading}
              autoFocus
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          variant={variant}
          onClick={handleConfirm}
          disabled={loading || isConfirmDisabled}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Procesando...
            </>
          ) : (
            confirmText
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDialog;
